/**
 * Anexos de solicitação/tarefa — as regras que não tocam em rede.
 *
 * Separado de `anexosTarefa.ts` (que fala com o Storage e com o banco) pelo
 * mesmo motivo de `cancelamentoSicoobResposta.ts`: assim a regra tem teste
 * próprio, rodando no Node sem subir nada.
 *
 * Existe por causa da solicitação 4906 (Alice Karoline, 31/08/2026): anexar
 * documento funcionava quase sempre e falhava ~1 vez a cada 100, sem reprodução
 * possível. Uma das três causas era daqui — o caminho do arquivo era
 * `Date.now()_nome.ext`, e dois arquivos de mesmo nome escolhidos juntos caem no
 * mesmo milissegundo, geram o mesmo caminho e o segundo upload volta
 * "already exists". As outras duas (perda por sobrescrita e arquivo órfão) são
 * tratadas em `anexosTarefa.ts` e na migration 20260921180000.
 */

export const BUCKET_ANEXOS = 'tarefas-anexos';

/** Tentativas totais de upload (a primeira + as repetições). */
export const TENTATIVAS_UPLOAD = 3;

/**
 * Caminho do arquivo no bucket.
 *
 * Formato: `<timestamp>-<aleatório>_<nome-original-sanitizado>.<ext>`
 *
 * O timestamp fica na frente porque ordena o bucket por data, que é como se
 * procura um arquivo por lá. O sufixo aleatório é o que garante unicidade — o
 * milissegundo sozinho não garante, e era essa a colisão. O nome original
 * sobrevive sanitizado para o arquivo ser reconhecível fora do VigorHub.
 */
export function caminhoAnexo(nomeOriginal: string, aleatorio?: string): string {
    // `split('.').pop()` sozinho mente quando NÃO há ponto: devolve o nome
    // inteiro, e 'anexo' virava 'anexo.anexo'. Só há extensão se houver ponto
    // depois do primeiro caractere.
    const ponto = nomeOriginal.lastIndexOf('.');
    const temExtensao = ponto > 0 && ponto < nomeOriginal.length - 1;

    const ext = temExtensao
        ? (nomeOriginal.slice(ponto + 1).replace(/[^\w]+/g, '').slice(0, 10) || 'bin')
        : 'bin';

    // `|| 'arquivo'` não bastava: '###.png' vira '_' (não vazio) e o caminho
    // saía como '..._-_.png'. Um nome que sobra só em separadores é tão
    // inútil quanto um vazio.
    const baseBruta = (temExtensao ? nomeOriginal.slice(0, ponto) : nomeOriginal)
        .replace(/[^\w\-]+/g, '_')
        .slice(0, 60);

    const base = baseBruta.replace(/[_\-]/g, '') ? baseBruta : 'arquivo';

    // Sem `crypto.randomUUID` em contexto não seguro (http://) — cai no Math.random,
    // que aqui basta: o timestamp já separa, o sufixo só desempata o mesmo ms.
    const sufixo = aleatorio ?? (
        globalThis.crypto?.randomUUID?.().slice(0, 8)
        ?? Math.random().toString(36).slice(2, 10)
    );

    return `${Date.now()}-${sufixo}_${base}.${ext}`;
}

/**
 * Nome legível de um anexo, a partir da URL.
 *
 * ⚠️ Aceita os DOIS formatos de propósito: `<ts>-<rand>_nome` (a partir de
 * 21/09/2026) e `<ts>_nome` (anterior). Anexos mais antigos que isso não têm o
 * nome original preservado no caminho — mostra o que der.
 */
export function nomeExibicaoAnexo(url: string): string {
    try {
        const arquivo = decodeURIComponent(url.split('/').pop() || '');
        return arquivo.replace(/^\d+(-[A-Za-z0-9]+)?_/, '') || 'Anexo';
    } catch {
        return 'Anexo';
    }
}

/** Erro de upload que vale repetir? Duplicidade e recusa de permissão, não. */
export function valeRepetir(erro: unknown): boolean {
    const msg = String((erro as any)?.message ?? erro ?? '').toLowerCase();
    if (!msg) return true;
    if (msg.includes('already exists') || msg.includes('duplicate')) return false;
    if (msg.includes('unauthorized') || msg.includes('not authorized')
        || msg.includes('permission') || msg.includes('row-level security')) return false;
    if (msg.includes('exceeded') || msg.includes('too large') || msg.includes('payload')) return false;
    return true;
}
